import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import PhotoUploader from "../components/PhotoUploader";
import { ChevronRight, ChevronLeft, Sparkles, CheckCircle2 } from "lucide-react";

const STEPS = ["Category", "Photos", "Details", "Condition", "Auction", "Shipping", "Review"];

export default function Sell() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [tree, setTree] = useState([]);
  const [topId, setTopId] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [form, setForm] = useState({
    category_id: "", title: "", description: "", brand: "", condition: "very_good",
    condition_notes: "", photos: [], starting_bid: 1000, reserve_price: "",
    min_increment: 100, duration_hours: 168, shipping_price: 250, shipping_method: "Standard Courier",
    location: "",
  });

  useEffect(() => { if (!loading && !user) nav("/signin?next=/sell"); }, [user, loading, nav]);
  useEffect(() => { api.get("/categories/tree").then(r => setTree(r.data)); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const topNode = tree.find(t => t.id === topId);
  const subs = topNode?.subcategories || [];

  const addPhotoUrl = () => {
    const url = prompt("Paste a photo URL (Unsplash / Pexels / your CDN):");
    if (url) set("photos", [...form.photos, url]);
  };
  const removePhoto = (i) => set("photos", form.photos.filter((_, idx) => idx !== i));

  const askAI = async () => {
    setAiBusy(true);
    try {
      const cat = tree.flatMap(t => [t, ...(t.subcategories || [])]).find(c => c.id === form.category_id);
      const r = await api.post("/ai/listing-suggest", {
        category_hint: cat?.name || "",
        notes: `${form.title}. ${form.description}. Brand: ${form.brand}. Condition: ${form.condition}`,
        photos: form.photos,
      });
      const s = r.data?.suggestions;
      if (s?.title) set("title", s.title);
      if (s?.description) set("description", s.description);
      toast.success("AI suggestions applied", { description: "Review and edit before publishing." });
    } catch { toast.error("AI suggest unavailable"); }
    finally { setAiBusy(false); }
  };

  const canNext = () => {
    if (step === 0) return !!form.category_id;
    if (step === 1) return form.photos.length >= 1;
    if (step === 2) return form.title.length > 5 && form.description.length > 20;
    if (step === 4) return Number(form.starting_bid) > 0 && Number(form.duration_hours) > 0;
    return true;
  };

  const submit = async () => {
    try {
      const payload = { ...form,
        starting_bid: Number(form.starting_bid),
        reserve_price: form.reserve_price ? Number(form.reserve_price) : null,
        min_increment: Number(form.min_increment),
        duration_hours: Number(form.duration_hours),
        shipping_price: Number(form.shipping_price),
      };
      const r = await api.post("/listings", payload);
      toast.success("Listing submitted!", { description: r.data.status === "live" ? "Live now." : "Under review." });
      nav(`/auction/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to submit"); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
      <div className="mb-2 text-xs uppercase tracking-[0.2em] font-bold text-amber-600">List an item</div>
      <h1 className="text-3xl md:text-4xl font-extrabold">Sell on AuctionBulls</h1>
      <div className="mt-2 inline-flex items-center gap-2 text-sm text-emerald-700 font-semibold">
        <CheckCircle2 className="h-4 w-4" /> Listing fee: ₹0 — Pay only 8% when it sells
      </div>

      {/* Stepper */}
      <div className="mt-8 grid grid-cols-7 gap-1">
        {STEPS.map((s, i) => (
          <div key={s} className={`h-1.5 rounded-full ${i <= step ? "bg-amber-500" : "bg-slate-200 dark:bg-slate-800"}`} />
        ))}
      </div>
      <div className="mt-4 text-sm text-slate-500">Step {step + 1} of {STEPS.length}: <span className="font-bold text-slate-900 dark:text-white">{STEPS[step]}</span></div>

      <div data-testid={`listing-wizard-step-${step + 1}`} className="mt-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 md:p-8 space-y-5">
        {step === 0 && (
          <div className="space-y-5">
            <div>
              <Label>Primary category</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                {tree.map(c => (
                  <button key={c.id} type="button" onClick={() => { setTopId(c.id); set("category_id", c.id); }}
                    className={`text-left p-4 rounded-xl border-2 ${topId === c.id ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30" : "border-slate-200 dark:border-slate-800"}`}>
                    <div className="font-bold text-sm">{c.name}</div>
                    <div className="text-xs text-slate-500 mt-1 capitalize">
                      {c.subcategories?.length ? `${c.subcategories.length} subcategories` : `Risk: ${c.risk_level}`}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {subs.length > 0 && (
              <div>
                <Label>Subcategory</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                  {subs.map(s => (
                    <button key={s.id} type="button" onClick={() => set("category_id", s.id)}
                      className={`text-left px-3 py-2 rounded-lg border text-sm ${form.category_id === s.id ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30 font-semibold" : "border-slate-200 dark:border-slate-800"}`}
                      data-testid={`sub-${s.slug}`}>
                      {s.name}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">Choose the most specific match. Property & Businesses require enhanced verification and documentation review.</p>
              </div>
            )}
          </div>
        )}
        {step === 1 && (
          <div className="space-y-3">
            <Label>Photos (min. 1 · recommended 4+)</Label>
            <PhotoUploader
              photos={form.photos}
              onChange={(list) => set("photos", list)}
              max={12}
            />
            <p className="text-xs text-slate-500">Drag to reorder. First photo is your cover. Real uploads — no URLs required.</p>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Title & Description</Label>
              <Button variant="outline" size="sm" onClick={askAI} disabled={aiBusy || !form.category_id}>
                <Sparkles className="h-4 w-4 mr-1" /> {aiBusy ? "Thinking..." : "AI Suggest"}
              </Button>
            </div>
            <Input data-testid="listing-title" placeholder="Concise, specific title" value={form.title} onChange={e => set("title", e.target.value)} />
            <Textarea data-testid="listing-description" rows={6} placeholder="Describe the item, its history, defects, accessories..." value={form.description} onChange={e => set("description", e.target.value)} />
            <Input placeholder="Brand (optional)" value={form.brand} onChange={e => set("brand", e.target.value)} />
          </div>
        )}
        {step === 3 && (
          <div className="space-y-3">
            <Label>Condition</Label>
            <Select value={form.condition} onValueChange={v => set("condition", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="like_new">Like New</SelectItem>
                <SelectItem value="very_good">Very Good</SelectItem>
                <SelectItem value="good">Good</SelectItem>
                <SelectItem value="fair">Fair</SelectItem>
                <SelectItem value="for_parts">For Parts / Restoration</SelectItem>
              </SelectContent>
            </Select>
            <Textarea rows={4} placeholder="Condition notes: scratches, defects, repairs..." value={form.condition_notes} onChange={e => set("condition_notes", e.target.value)} />
          </div>
        )}
        {step === 4 && (
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Starting bid (₹)</Label><Input data-testid="listing-starting-bid" type="number" value={form.starting_bid} onChange={e => set("starting_bid", e.target.value)} /></div>
            <div><Label>Reserve price (optional)</Label><Input type="number" value={form.reserve_price} onChange={e => set("reserve_price", e.target.value)} /></div>
            <div><Label>Min. bid increment (₹)</Label><Input type="number" value={form.min_increment} onChange={e => set("min_increment", e.target.value)} /></div>
            <div><Label>Duration (hours)</Label><Input type="number" value={form.duration_hours} onChange={e => set("duration_hours", e.target.value)} /></div>
          </div>
        )}
        {step === 5 && (
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Shipping price (₹)</Label><Input type="number" value={form.shipping_price} onChange={e => set("shipping_price", e.target.value)} /></div>
            <div>
              <Label>Shipping method</Label>
              <Select value={form.shipping_method} onValueChange={v => set("shipping_method", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Standard Courier">Standard Courier</SelectItem>
                  <SelectItem value="Insured Express">Insured Express</SelectItem>
                  <SelectItem value="White-glove Delivery">White-glove Delivery</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>Location</Label><Input placeholder="City, State" value={form.location} onChange={e => set("location", e.target.value)} /></div>
          </div>
        )}
        {step === 6 && (
          <div className="space-y-3 text-sm">
            <h3 className="font-bold text-lg">Review your listing</h3>
            <div className="grid grid-cols-2 gap-2">
              <Cell k="Title" v={form.title} />
              <Cell k="Category" v={tree.flatMap(t => [t, ...(t.subcategories || [])]).find(c => c.id === form.category_id)?.name} />
              <Cell k="Photos" v={`${form.photos.length}`} />
              <Cell k="Condition" v={form.condition} />
              <Cell k="Starting bid" v={`₹${form.starting_bid}`} />
              <Cell k="Duration" v={`${form.duration_hours}h`} />
              <Cell k="Shipping" v={`₹${form.shipping_price} · ${form.shipping_method}`} />
              <Cell k="Success fee" v="8% on sale" />
            </div>
            <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 p-3 text-emerald-800 dark:text-emerald-300 text-xs">
              By submitting, you agree the item is accurately described. Low-photo or high-risk categories may enter review.
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-between">
        <Button variant="outline" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}><ChevronLeft className="h-4 w-4" /> Back</Button>
        {step < STEPS.length - 1 ? (
          <Button data-testid="listing-wizard-next-btn" onClick={() => setStep(s => s + 1)} disabled={!canNext()} className="bg-slate-900 text-white">Continue <ChevronRight className="h-4 w-4" /></Button>
        ) : (
          <Button data-testid="listing-wizard-submit" onClick={submit} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold">Publish Listing</Button>
        )}
      </div>
    </div>
  );
}

function Cell({ k, v }) {
  return <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800"><div className="text-[10px] uppercase text-slate-500 font-bold">{k}</div><div className="font-semibold mt-0.5">{v || "—"}</div></div>;
}
